import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CustomerLayout from "@/components/CustomerLayout";
import AddressForm from "@/components/AddressForm";
import InstallApp from "@/components/InstallApp";
import MembershipCard from "@/components/MembershipCard";
import { User, MapPin, Package, Bell, LifeBuoy, LogOut, Trash2, Plus, Pencil, Star } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { data: addresses, refetch } = useQuery({ queryKey: ["addresses"], queryFn: () => api.get("/addresses").then((r) => r.data) });
  const { data: notifs } = useQuery({ queryKey: ["notifs"], queryFn: () => api.get("/notifications").then((r) => r.data) });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const save = async (addr) => {
    if (editing) { await api.patch(`/addresses/${editing.id}`, addr); }
    else { await api.post("/addresses", addr); }
    setShowForm(false); setEditing(null); refetch(); toast.success("Address saved");
  };
  const setDefault = async (a) => { await api.patch(`/addresses/${a.id}`, { ...stripAddr(a), is_default: true }); refetch(); toast.success("Default address updated"); };
  const delAddr = async (id) => { await api.delete(`/addresses/${id}`); refetch(); toast.success("Address removed"); };
  const doLogout = () => { logout(); toast.success("Logged out"); nav("/"); };

  return (
    <CustomerLayout>
      <div className="mx-auto max-w-2xl space-y-5">
        <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand text-white"><User className="h-8 w-8" /></div>
          <div>
            <h1 className="font-head text-xl font-extrabold text-slate-900">{user?.name}</h1>
            <p className="text-sm text-slate-500">{user?.email}</p>
            {user?.phone && <p className="text-sm text-slate-500">{user.phone}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => nav("/orders")} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left hover:border-brand/40">
            <Package className="h-5 w-5 text-brand" /><span className="font-semibold text-slate-800">My Orders</span>
          </button>
          <button onClick={() => nav("/support")} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left hover:border-brand/40">
            <LifeBuoy className="h-5 w-5 text-brand" /><span className="font-semibold text-slate-800">Support</span>
          </button>
        </div>

        <MembershipCard />

        <InstallApp variant="full" />

        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-head font-bold text-slate-900"><MapPin className="h-5 w-5 text-brand" /> Saved addresses</h2>
            {!showForm && <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="account-add-address-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-brand"><Plus className="h-4 w-4" /> Add</button>}
          </div>
          <div className="space-y-2">
            {(addresses || []).map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-xl border border-slate-100 p-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{a.label} {a.is_default && <span className="ml-1 rounded bg-brand-soft px-1.5 py-0.5 text-[10px] font-bold text-brand">DEFAULT</span>}</p>
                  <p className="text-xs text-slate-500">{a.line}, {a.city} {a.state} {a.pincode}</p>
                </div>
                <div className="flex items-center gap-2">
                  {!a.is_default && <button onClick={() => setDefault(a)} title="Set default" className="text-slate-300 hover:text-amber-500"><Star className="h-4 w-4" /></button>}
                  <button onClick={() => { setEditing(a); setShowForm(true); }} className="text-slate-300 hover:text-brand"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => delAddr(a.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
            {!addresses?.length && !showForm && <p className="text-sm text-slate-400">No saved addresses yet.</p>}
          </div>
          {showForm && <div className="mt-3"><AddressForm initial={editing ? stripAddr(editing) : null} onSubmit={save} onCancel={() => { setShowForm(false); setEditing(null); }} /></div>}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="mb-3 flex items-center gap-2 font-head font-bold text-slate-900"><Bell className="h-5 w-5 text-brand" /> Notifications</h2>
          <div className="space-y-2">
            {(notifs || []).slice(0, 6).map((n) => (
              <div key={n.id} className="rounded-xl border border-slate-100 p-3">
                <p className="text-sm font-semibold text-slate-800">{n.title}</p>
                <p className="text-xs text-slate-500">{n.message}</p>
              </div>
            ))}
            {!notifs?.length && <p className="text-sm text-slate-400">No notifications.</p>}
          </div>
        </section>

        <button onClick={doLogout} data-testid="logout-btn" className="flex w-full items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 py-3 font-bold text-red-600 hover:bg-red-100">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </CustomerLayout>
  );
}

function stripAddr(a) {
  const { id, user_id, _id, created_at, ...rest } = a;
  return rest;
}
